type MeshCommitTask<T> = {
  key: string;
  priority(): number;
  shouldRun(): boolean;
  task(): T;
  abortController: AbortController;
};

type QueuedMeshCommit<T> = MeshCommitTask<T> & {
  resolve(value: T): void;
  reject(reason: Error): void;
  abortListener(): void;
};

export class MeshCommitQueue<T> {
  private queue: Array<QueuedMeshCommit<T>> = [];
  private frameHandle: number | undefined;
  private disposed = false;

  public add(task: MeshCommitTask<T>): Promise<T> {
    if (this.disposed || task.abortController.signal.aborted) {
      return Promise.reject(this.createAbortError(task.key));
    }

    return new Promise<T>((resolve, reject) => {
      const abortListener = (): void => {
        const taskIndex = this.queue.findIndex(
          (queuedTask) => queuedTask.key === task.key
        );
        if (taskIndex === -1) {
          return;
        }

        const [abortedTask] = this.queue.splice(taskIndex, 1);
        abortedTask.abortController.signal.removeEventListener(
          'abort',
          abortedTask.abortListener
        );
        abortedTask.reject(this.createAbortError(abortedTask.key));
      };
      const queuedTask: QueuedMeshCommit<T> = {
        ...task,
        resolve,
        reject,
        abortListener,
      };

      task.abortController.signal.addEventListener('abort', abortListener, {
        once: true,
      });
      this.queue.push(queuedTask);
      this.scheduleFrame();
    });
  }

  public dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== undefined) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = undefined;
    }

    const tasks = this.queue;
    this.queue = [];
    tasks.forEach((task) => {
      task.abortController.signal.removeEventListener(
        'abort',
        task.abortListener
      );
      task.reject(this.createAbortError(task.key));
    });
  }

  private scheduleFrame(): void {
    if (
      this.disposed ||
      this.frameHandle !== undefined ||
      this.queue.length === 0
    ) {
      return;
    }

    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = undefined;
      this.processNext();
      this.scheduleFrame();
    });
  }

  private processNext(): void {
    this.queue.sort((a, b) => a.priority() - b.priority());
    const task = this.queue.shift();
    if (!task) {
      return;
    }

    task.abortController.signal.removeEventListener(
      'abort',
      task.abortListener
    );
    if (task.abortController.signal.aborted || !task.shouldRun()) {
      task.reject(this.createAbortError(task.key));
      return;
    }

    try {
      task.resolve(task.task());
    } catch (error) {
      task.reject(error as Error);
    }
  }

  private createAbortError(key: string): Error {
    const error = new Error(`Mesh commit aborted for task: ${key}`);
    error.name = 'AbortError';
    return error;
  }
}
